import { createFileRoute, Link } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import {
  CheckCircle2Icon,
  CreditCardIcon,
  ExternalLinkIcon,
  LayersIcon,
  MessagesSquareIcon,
} from 'lucide-react'

import { Button, Card, CardContent, Skeleton } from '@/components'
import { useToast } from '@/hooks'
import {
  ReservedCreditsIndicator,
  useAccount,
  useAccountLoaders,
  useLoadingSummary,
  useSummary,
  useUsage,
} from '@/modules/account'
import { useSupportTicket } from '@/modules/support'
import { formatNumber } from '@/utils'

export const Route = createFileRoute('/_authenticated/billing')({
  staticData: { title: 'Billing' },
  component: BillingPage,
})

const UPGRADE_PERKS = [
  'Custom credit packages',
  'Volume discounts',
  'Team access',
  'Priority support',
]

const PORTAL_PERKS = [
  'View and download invoices',
  'Update payment methods',
  'Manage subscription',
  'Cancel subscription',
]

function BillingPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-10 p-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">Billing</h1>
        <p className="text-muted-foreground text-sm">
          Your plan, upgrades, and payment details in one place.
        </p>
      </div>

      <Section
        title="Your current plan"
        description="What your account is on today."
      >
        <CurrentPlanCard />
      </Section>

      <Section
        title="Upgrade your plan"
        description="Talk to our team to find the right plan for your needs."
      >
        <UpgradeCard />
      </Section>

      <Section
        title="Manage billing"
        description="View invoices, update payment methods, and manage your subscription in our secure billing portal."
      >
        <BillingPortalCard />
      </Section>
    </div>
  )
}

function Section({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-muted-foreground max-w-lg text-sm">{description}</p>
      </div>
      {children}
    </section>
  )
}

function IconBubble({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <div className="bg-brand/10 flex size-11 shrink-0 items-center justify-center rounded-full">
      <Icon className="text-brand size-5" />
    </div>
  )
}

function Stat({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
        {label}
      </p>
      <div className="font-semibold">{children}</div>
    </div>
  )
}

function CurrentPlanCard() {
  const account = useAccount()
  const summary = useSummary()
  const loadingSummary = useLoadingSummary()
  const { counter } = useUsage()
  const isLegacy = account?.planKind === 'legacy_subscription'

  if (loadingSummary && !summary) {
    return <Skeleton className="h-24 w-full rounded-lg" />
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-6 p-6 sm:flex-row sm:items-center">
        <div className="flex items-center gap-4 sm:min-w-56">
          <IconBubble icon={LayersIcon} />
          <div>
            <p className="font-semibold">{account?.plan}</p>
            <p className="text-muted-foreground text-sm">1 page = 1 credit</p>
          </div>
        </div>
        {isLegacy ? (
          <Stat label="Pages this cycle">{counter}</Stat>
        ) : (
          <Stat label="Credits">
            <span className="flex items-center gap-1.5">
              {formatNumber(summary?.pagesCredits ?? 0)}
              <ReservedCreditsIndicator
                reserved={summary?.pagesCreditsReserved ?? 0}
                className="text-muted-foreground"
              />
            </span>
          </Stat>
        )}
        <Stat label="Status">
          <span className="bg-brand/10 text-brand inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium">
            Active
          </span>
        </Stat>
        {!isLegacy && (
          <div className="sm:ml-auto">
            <Button asChild theme="outline" size="md">
              <Link to="/credits" preload="intent">
                Buy credits
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function UpgradeCard() {
  const { openUpgradeTicket } = useSupportTicket()

  return (
    <ActionCard
      icon={MessagesSquareIcon}
      title="Contact us to upgrade"
      description="Our team is here to help you find the best plan for your firm."
      perks={UPGRADE_PERKS}
      action={
        <Button size="md" onClick={openUpgradeTicket}>
          Contact us
        </Button>
      }
    />
  )
}

function BillingPortalCard() {
  const { errorToast } = useToast()
  const { fetchCustomerPortalUrl } = useAccountLoaders()

  const openPortalInNewTab = async () => {
    try {
      const url = await fetchCustomerPortalUrl()
      window.open(url, '_blank')
    } catch (error) {
      errorToast('Failed to open billing portal', error as Error)
    }
  }

  return (
    <ActionCard
      icon={CreditCardIcon}
      title="Go to billing portal"
      description="You'll be taken to Stripe to securely manage your billing and subscription."
      perks={PORTAL_PERKS}
      action={
        <Button size="md" onClick={openPortalInNewTab}>
          Manage billing
          <ExternalLinkIcon />
        </Button>
      }
    />
  )
}

function ActionCard({
  icon,
  title,
  description,
  perks,
  action,
}: {
  icon: LucideIcon
  title: string
  description: string
  perks: string[]
  action: React.ReactNode
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-6 p-6 lg:flex-row lg:items-center">
        <div className="flex gap-4 lg:max-w-sm lg:flex-1">
          <IconBubble icon={icon} />
          <div className="space-y-1">
            <p className="font-semibold">{title}</p>
            <p className="text-muted-foreground text-sm">{description}</p>
          </div>
        </div>
        <ul className="space-y-2 lg:flex-1">
          {perks.map((perk) => (
            <li key={perk} className="flex items-center gap-2 text-sm">
              <CheckCircle2Icon className="text-brand size-4 shrink-0" />
              {perk}
            </li>
          ))}
        </ul>
        <div className="lg:ml-auto lg:self-center">{action}</div>
      </CardContent>
    </Card>
  )
}
